import { ENERGY_MASTERS_TENANT_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import { Circuit } from '../../../api/api-device';
import { useSiteAppliances, useSiteCircuits } from '../meter-setup/meterSetupApi';
import { useGetSwitchesQuery } from '../meter-setup/switch-configuration/switchApi';
import { useSite } from '../siteApi';
import {
  APPLIANCE_TYPE_TO_HOT_WATER_TYPE,
  DEFAULT_HOT_WATER_DAILY_RUNTIME_MINUTES,
  DEFAULT_HOT_WATER_POWER_RATING_KW,
} from './constants';
import { HotWaterDeviceFormData } from './system-details-form-types';

type ContactorControlledHotWaterDevice = HotWaterDeviceFormData & {
  applianceName: string;
  // A.K.A. the 'site_identifier' of the Site Device
  oemId: string;
};

/**
 * This hook gets all contactor-controlled hot water devices for the current site.
 * NOTE: we currently only return values for Energy Masters / CATCH sites, where we need to have the ability to update hot water details after the initial commissioning on the Configurator app.
 *
 * This is a hodgepodge that collates information from "circuits", "appliances" and "switches".
 * This could all be a lot cleaner if we treated hot water systems as "site devices" in their own right (and completed the deprecation of the switch endpoints).
 * But that will have to wait until we have resources to work on the Australian platform again.
 * @returns An object containing an array of contactor-controlled hot water devices, a loading state, as well as a boolean indicating if there is an unexpected unlinked switch.
 */
export const useGetContactorControlledHotWaterDevices = (): {
  contactorControlledHotWaterDevices: ContactorControlledHotWaterDevice[];
  /** True when a switch device has been set up without any links to loads. This is specifically for picking up CATCH Configurator issues.  */
  hasUnexpectedUnlinkedSwitch: boolean;
  isLoading: boolean;
} => {
  const { site } = useSite();
  const shouldGetData = site.tenant.tenant_id === ENERGY_MASTERS_TENANT_ID;
  const { siteAppliances, isLoading: isLoadingSiteAppliances } = useSiteAppliances({ skip: !shouldGetData });
  const { siteCircuits, isLoadingSiteCircuits } = useSiteCircuits({ skip: !shouldGetData });
  let contactorControlledHotWaterAppliances = siteAppliances
    .filter(
      (a) =>
        ['load_hot_water', 'load_hot_water_solar', 'load_hot_water_heat_pump'].includes(a.appliance_type) &&
        a.control_device_id != null
    )
    .sort((a, b) => a.appliance_id - b.appliance_id);
  const { data: switchDevices, isLoading: isSwitchesQueryLoading } = useGetSwitchesQuery(site.clipsal_solar_id, {
    skip:
      !shouldGetData ||
      // Skip the query if we failed to find any contactor-controlled hot water loads, unless there are `load_other` loads whose identity is as yet unknown.
      (!contactorControlledHotWaterAppliances.length &&
        !siteAppliances.filter((a) => a.appliance_type === 'load_other').length),
  });
  // Include contactor-linked `load_other` appliances for CATCH sites that haven't yet been fully set up
  // If there are multiple, one of these loads may not be a hot water system but we will ask the installer
  if (contactorControlledHotWaterAppliances.length === 0) {
    contactorControlledHotWaterAppliances = siteAppliances
      .filter(
        (a) =>
          a.appliance_type === 'load_other' &&
          a.control_device_id != null &&
          !!switchDevices?.find((s) => s.id === a.control_device_id)
      )
      .sort((a, b) => a.appliance_id - b.appliance_id);
  }
  // `hasUnexpectedUnlinkedSwitch` is true when we have `load_other` appliances and one or more CATCH switches that don't connect to anything.
  // This is specifically for picking up CATCH Configurator issues.
  const hasUnexpectedUnlinkedSwitch =
    siteAppliances.filter((a) => a.appliance_type === 'load_other').length > 0 &&
    !!switchDevices?.length &&
    // One or more of the switches does not connect to any of the appliances.
    switchDevices.some((switchDevice) => !siteAppliances.some((a) => a.control_device_id === switchDevice.id));

  const isLoading = isLoadingSiteAppliances || isLoadingSiteCircuits || isSwitchesQueryLoading;
  return {
    contactorControlledHotWaterDevices: isLoading
      ? []
      : contactorControlledHotWaterAppliances.map((appliance) => {
          const linkedSwitchDevice = switchDevices?.find((s) => s.id === appliance.control_device_id);
          return {
            applianceId: appliance.appliance_id,
            switchSiteDeviceId: appliance.control_device_id!,
            circuits: appliance.circuits.map((circuitId) =>
              siteCircuits.find((sc) => sc.id === circuitId)
            ) as Circuit[],
            hotWaterType: APPLIANCE_TYPE_TO_HOT_WATER_TYPE[appliance.appliance_type],
            activeForSmartSave: linkedSwitchDevice?.intelligent_control?.active ?? false,
            powerRatingkW:
              linkedSwitchDevice?.intelligent_control?.export_threshold_kw ?? DEFAULT_HOT_WATER_POWER_RATING_KW,
            dailyRuntimeMinutes:
              linkedSwitchDevice?.intelligent_control?.daily_min_runtime_minutes ??
              DEFAULT_HOT_WATER_DAILY_RUNTIME_MINUTES,
            manufacturerString: linkedSwitchDevice?.intelligent_control?.manufacturer_string,
            modelNumber: linkedSwitchDevice?.intelligent_control?.model_number,
            applianceName: appliance.appliance_name,
            oemId: linkedSwitchDevice?.oem_switch_id ?? '',
          };
        }),
    isLoading,
    hasUnexpectedUnlinkedSwitch,
  };
};
